import { Router, useLocation } from "@reach/router"
import * as Sentry from "@sentry/react"
import { BrowserTracing } from "@sentry/tracing"
import { navigate } from "gatsby"
import { useCallback } from "react"

import {
  RouteGuard as GlobalRouteGuard,
  Spinner,
  useQueryState,
} from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { storageService } from "@trueskin-web/services"
import { Routes } from "@trueskin-web/translations"

import ErrorFallback from "./components/error-fallback"
import Start from "./start"

if (process.env.GATSBY_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.GATSBY_SENTRY_DSN,
    environment: process.env.GATSBY_ACTIVE_ENV,
    attachStacktrace: true,
    normalizeDepth: 8,
    replaysSessionSampleRate: process.env.GATSBY_SENTRY_SAMPLE_RATE || 0.1,
    integrations: [new BrowserTracing(), new Sentry.Replay()],
  })
}

const storageKeys = storageService.getStorageKeys()

const Redirect = () => {
  const { getParam, getAppendedSearchString, getSearchString } = useQueryState()
  const urlVoucher = getParam(storageKeys.VOUCHER_URL)
  let query = getSearchString()
  if (urlVoucher) {
    query = getAppendedSearchString("voucher_code", urlVoucher)
  }
  const { pathname } = useLocation()
  const pathIsFunnel = pathname.startsWith(Routes.App.Start.url)
  const { user } = useAuth()

  if (!pathIsFunnel) {
    // reload window tab
    window.location.reload()
    return null
  }

  const {
    treatmentPlan,
    subscription,
    redoInitialQuestionnaire = false,
  } = user || {}

  const {
    consultations: [{ type: consultationType, status: consultationStatus }],
    status: subscriptionStatus,
  } = subscription || { consultations: [{}] }

  if (
    !subscription ||
    (consultationType === "initial" &&
      consultationStatus === "waitingfileupload") ||
    redoInitialQuestionnaire
  ) {
    navigate(Routes.App.Start.url + query, { replace: true })
    return null
  }

  if (
    !treatmentPlan &&
    (subscriptionStatus === "canceled" || subscriptionStatus === "expired")
  ) {
    navigate(Routes.App.Account.url + query, { replace: true })
    return null
  }

  navigate(Routes.App.Home.url + query, { replace: true })
  return null
}

const RouteGuard = (props) => (
  <GlobalRouteGuard fallback={Redirect} {...props} />
)

const App = () => {
  const { isLoading: isUserLoading, error: userError, user } = useAuth()

  const { subscription, redoInitialQuestionnaire = false } = user || {}

  const {
    consultations: [consultation],
  } = subscription || { consultations: [] }

  const { type: consultationType, status: consultationStatus } =
    consultation || {}

  if (user?.id) {
    Sentry.setUser({
      id: user.id,
    })
  }

  const canAccessStart = useCallback(
    () =>
      !subscription ||
      (consultationType === "initial" &&
        consultationStatus === "waitingfileupload") ||
      redoInitialQuestionnaire,
    [
      consultationStatus,
      consultationType,
      subscription,
      redoInitialQuestionnaire,
    ]
  )

  if (isUserLoading) {
    return <Spinner isFullPage />
  }

  if (userError) {
    return <ErrorFallback error={userError} />
  }

  return (
    <Router>
      <RouteGuard
        path={`${Routes.App.Start.path}/*`}
        condition={canAccessStart}
        component={Start}
      />

      <Redirect default />
    </Router>
  )
}

export default App
